import { safeLocale } from "@/utilities/i18n/safeLocale";

export interface FormatNumberOptions {
  /** Decimal places (default 0). With `fixed`, always shown; otherwise the maximum. */
  precision?: number;
  /** Returned for a value that is not a finite number (default "---"). */
  missing?: string;
  /**
   * Show exactly `precision` decimals (default true). False: up to `precision`, trailing zeros
   * dropped.
   */
  fixed?: boolean;
  /** Thousands separators (default true). */
  useGrouping?: boolean;
}

/**
 * Formats a number for display in the given language.
 *
 * Separate from `useFormattedNumber` because a grid `valueFormatter` is a plain callback and
 * cannot call a hook — and a cell that formats differently from the export it feeds is a bug
 * waiting to be reported. Both go through this.
 *
 * The language tag goes through `safeLocale`, so a malformed tag formats as de-DE instead of
 * throwing a RangeError.
 */
export function formatNumber(
  value: number | null | undefined,
  language: string | undefined,
  options: FormatNumberOptions = {},
): string {
  const { precision = 0, missing = "---", fixed = true, useGrouping = true } = options;

  if (typeof value !== "number" || !Number.isFinite(value)) {
    return missing;
  }

  const locale = safeLocale(language ?? "");

  return value.toLocaleString(locale, {
    minimumFractionDigits: fixed ? precision : 0,
    maximumFractionDigits: precision,
    useGrouping,
  });
}
